import React, {
    useEffect,
    useRef,
    useState,
    useMemo,
    ReactElement,
    useCallback,
} from 'react';
import {InputGroup, Button, Form} from 'react-bootstrap';
import {useFormikContext} from 'formik';
import {Message, useNotificationContext} from 'contexts/NotificationContext';
import {useProductContext} from 'contexts/ProductContext';
import {useAppContext} from 'contexts/ApplicationContext';
import {OverlayTrigger, OverlayType} from 'shared/components/OverlayTrigger';
import {ChevronDown, ChevronUp} from 'assets';
import {validationErrorAppliesToField} from 'shared/validator';
import styled from 'styled-components';
import {ProductDataStore} from 'components/customer/Preview3D/usePreview3DData';
import {Structure} from 'components/customer/Product/entity/Structure';
import {omit} from 'lodash';

enum MouseEvent {
    NONE = 'none',
    INCREASE = 'increase',
    DECREASE = 'decrease',
}

type NumberType = {
    name: string;
    value: string | number;
    selectHandler: (name: string, value: string | number) => void;
    hasFormik?: boolean;
    noSpinners?: boolean;
    increaseIcon?: ReactElement | string;
    decreaseIcon?: ReactElement | string;
    reverseIcons?: boolean;
    step?: number;
    cycle?: boolean;
    showDialog?: () => void;
    hideDialog?: () => void;
    setCurrentTab?: () => void;
    formStructure?: Structure[];
    randomId?: string;
    fieldSetIndex?: number;
    max?: number;
    min?: number;
    isInvalid?: boolean;
    isQFP?: boolean;
    in_sizes_tab?: boolean;
    className?: string;
    productDataStore?: ProductDataStore;
    fieldIndex?: number;
    inSpecsTab?: boolean;
    fieldSetGroupName?: string;
};

export const Number = ({
    name,
    value,
    selectHandler,
    hasFormik = true,
    noSpinners = false,
    increaseIcon = ChevronUp,
    decreaseIcon = ChevronDown,
    reverseIcons = false,
    step = 1,
    fieldSetIndex,
    max,
    min,
    isQFP = false,
    isInvalid = false,
    fieldIndex,
    in_sizes_tab: inSizesTab,
    ...otherParams
}: NumberType) => {
    const propsToChildren = omit(otherParams, [
        'cycle',
        'showDialog',
        'hideDialog',
        'setCurrentTab',
        'formStructure',
        'randomId',
        'inSpecsTab',
        'fieldSetGroupName',
        'productDataStore',
    ]);

    const formik = hasFormik
        ? useFormikContext<{[key: string]: number}>()
        : undefined;

    const [localValue, setLocalValue] = useState(value);
    const [mouseHoldEvent, setMouseHoldEvent] = useState(MouseEvent.NONE);
    const {messages} = useNotificationContext();
    const productSpecificData = useProductContext<{fieldIndex: number}>();
    const {smallLayout} = useAppContext();

    const toolTip = useMemo(() => {
        const errors = messages.errors;
        let errorMessages: Message[];

        if (
            productSpecificData &&
            productSpecificData.hasOwnProperty('fieldIndex') &&
            productSpecificData.fieldIndex > -1 &&
            messages.errors.hasOwnProperty(productSpecificData.fieldIndex) &&
            !Array.isArray(messages.errors)
        ) {
            errorMessages = messages.errors[productSpecificData.fieldIndex];
        } else {
            errorMessages = errors as Message[];
        }

        if (
            errorMessages.length &&
            hasFormik &&
            formik?.errors &&
            ['door_hinge_amount_1'].includes(name)
        ) {
            errorMessages = errorMessages?.filter((e) =>
                e.fields?.some((f) => Object.keys(formik.errors).includes(f))
            );
        }

        if (isInvalid && errorMessages.length) {
            const tooltips = errorMessages
                .filter((error) =>
                    validationErrorAppliesToField(error, name, fieldSetIndex)
                )
                .map((error) => error.message);

            if (tooltips.length) {
                if (smallLayout) {
                    tooltips[0] = `* ${tooltips[0]}`;
                    return tooltips.join('\n* ');
                } else {
                    return (
                        <ul>
                            {tooltips.map((tooltip) => {
                                return <li key={tooltip}>{tooltip}</li>;
                            })}
                        </ul>
                    );
                }
            }
        }
    }, [isInvalid, messages, smallLayout]);

    const intervalRef = useRef<number>();
    const timeoutRef = useRef<number>();

    const updateValue = useCallback(
        (name: string, latestValue: number | string) => {
            if (value != latestValue) {
                if (typeof selectHandler !== 'undefined' && selectHandler)
                    selectHandler(name, latestValue);
                else {
                    if (formik) {
                        void formik.setFieldValue(name, latestValue);
                    }
                }
            }
        },
        [formik, value]
    );

    const updateLocalValue = useCallback((mouseEvent: MouseEvent) => {
        timeoutRef.current = window.setTimeout(() => {
            window.clearInterval(intervalRef.current);
            intervalRef.current = window.setInterval(() => {
                if (mouseEvent == MouseEvent.INCREASE) {
                    setLocalValue((currentValue: number) => {
                        if (
                            (typeof max !== 'undefined' &&
                                currentValue < max) ||
                            typeof max === 'undefined'
                        )
                            return currentValue + 1;
                        else return currentValue;
                    });
                } else if (mouseEvent == MouseEvent.DECREASE) {
                    setLocalValue((currentValue: number) => {
                        if (
                            (typeof min !== 'undefined' &&
                                currentValue > min) ||
                            typeof min === 'undefined'
                        )
                            return currentValue - 1;
                        else return currentValue;
                    });
                }
            }, 100);
        }, 800);
    }, []);

    useEffect(() => {
        value != localValue && setLocalValue(value);
    }, [value]);

    useEffect(() => {
        if (mouseHoldEvent && mouseHoldEvent != MouseEvent.NONE) {
            updateLocalValue(mouseHoldEvent);
        } else {
            window.clearTimeout(timeoutRef.current);
            window.clearInterval(intervalRef.current);
            value != localValue && updateValue(name, localValue);
        }
    }, [mouseHoldEvent]);

    const getCorrectValue = useCallback((number: string | number) => {
        number = parseFloat(String(number));
        number = number % 1 === 0 ? parseInt(String(number)) : number;

        return number;
    }, []);

    const focusInput = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
        e.currentTarget.closest('.cbc-number').querySelector('input').focus();
    }, []);

    const increase = useCallback(
        (e: React.MouseEvent<HTMLButtonElement>) => {
            if (
                (typeof max !== 'undefined' &&
                    parseFloat(String(localValue)) < max) ||
                typeof max === 'undefined'
            ) {
                const newValue = getCorrectValue(localValue) + step;
                setLocalValue(newValue);
                updateValue(name, newValue);
            }

            focusInput(e);
        },
        [
            max,
            localValue,
            step,
            name,
            setLocalValue,
            updateValue,
            getCorrectValue,
            focusInput,
        ]
    );

    const decrease = useCallback(
        (e: React.MouseEvent<HTMLButtonElement>) => {
            if (
                (typeof min !== 'undefined' &&
                    parseFloat(String(localValue)) > min) ||
                typeof min === 'undefined'
            ) {
                const newValue = getCorrectValue(localValue) - step;
                setLocalValue(newValue);
                updateValue(name, newValue);
            }

            focusInput(e);
        },
        [min, step, name, localValue, setLocalValue, updateValue, focusInput]
    );

    const handleBlurCustom = useCallback(
        (e: React.FocusEvent | React.KeyboardEvent) => {
            if (formik) {
                formik.handleBlur(e);
            }

            if (value != localValue) {
                updateValue(name, parseFloat(String(localValue)));
            }
        },
        [value, localValue, formik, name, updateValue]
    );

    const enterKeyPressed = useCallback(
        (event: React.KeyboardEvent) => {
            if (event.key === 'Enter') {
                handleBlurCustom(event);
                if (formik) {
                    void formik.submitForm();
                }
            }
        },
        [formik, handleBlurCustom]
    );

    const handleChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const newValue = parseInt(e.target.value);

            if (e.target.value == '') {
                setLocalValue(min || 0);
                return;
            }

            if (max && newValue > max) {
                return;
            }

            if (min && newValue < min) {
                return;
            }

            setLocalValue(newValue);
        },
        [max, min, setLocalValue]
    );

    const mouseHoldEventHandler = useCallback(
        (event: MouseEvent) => () => {
            setMouseHoldEvent(event);
        },
        []
    );

    let formControl = (
        <NumberInputGroup
            $inline={reverseIcons}
            className="cbc-number"
            style={{width: inSizesTab ? '150px' : 'auto'}}>
            <Form.Control
                {...propsToChildren}
                type="number"
                onKeyUp={enterKeyPressed}
                name={name}
                value={localValue}
                onChange={handleChange}
                onBlur={handleBlurCustom}
                isInvalid={isInvalid}
                data-cy={`${name}-${fieldIndex + 1}`}
                min={typeof min != 'undefined' ? min : undefined}
                max={typeof max != 'undefined' ? max : undefined}
                inputMode="numeric"
            />
            <InputGroupAppend
                $inline={reverseIcons}
                className={reverseIcons ? 'flex-row-reverse' : ''}>
                <InputButton
                    $inline={reverseIcons}
                    tabIndex={-1}
                    variant="outline-secondary"
                    className="number-spinner"
                    onClick={increase}
                    data-cy={`${name}-increase-${fieldIndex + 1}`}
                    onMouseDown={mouseHoldEventHandler(MouseEvent.INCREASE)}
                    onMouseUp={mouseHoldEventHandler(MouseEvent.NONE)}>
                    {typeof increaseIcon === 'string' ? (
                        <img
                            style={{width: '52%'}}
                            src={`data:image/svg+xml;base64, ${btoa(
                                increaseIcon
                            )}`}
                            alt="Increase number"
                        />
                    ) : (
                        increaseIcon
                    )}
                </InputButton>
                <InputButton
                    $inline={reverseIcons}
                    tabIndex={-1}
                    variant="outline-secondary"
                    className="number-spinner"
                    onClick={decrease}
                    data-cy={`${name}-decrease-${fieldIndex + 1}`}
                    onMouseDown={mouseHoldEventHandler(MouseEvent.DECREASE)}
                    onMouseUp={mouseHoldEventHandler(MouseEvent.NONE)}>
                    {typeof decreaseIcon === 'string' ? (
                        <img
                            style={{width: '52%'}}
                            src={`data:image/svg+xml;base64, ${btoa(
                                decreaseIcon
                            )}`}
                            alt="Decrease number"
                        />
                    ) : (
                        decreaseIcon
                    )}
                </InputButton>
            </InputGroupAppend>
        </NumberInputGroup>
    );

    if (noSpinners) {
        formControl = (
            <Form.Control
                {...propsToChildren}
                type="number"
                name={name}
                value={localValue}
                onChange={handleChange}
                onBlur={handleBlurCustom}
                onKeyUp={enterKeyPressed}
                isInvalid={isInvalid}
                data-cy={`${name}-${fieldIndex + 1}`}
                min={typeof min != 'undefined' ? min : undefined}
                max={typeof max != 'undefined' ? max : undefined}
            />
        );
    }

    // I AM A POPOVER
    if (isInvalid && typeof toolTip !== 'undefined') {
        return (
            <OverlayTrigger
                className="error-popover"
                as={OverlayType.Popover}
                overlay={toolTip}
                placement={isQFP ? 'auto-start' : 'right-end'}>
                {formControl}
            </OverlayTrigger>
        );
    }

    return formControl;
};

export const NumberInputGroup = styled(InputGroup)`
    max-height: 38px;
    width: 60%;
    background: white !important;

    > input {
        border-right: 0 !important;
    }

    ${({$inline}) => {
        if ($inline) {
            return `        
            flex-wrap: nowrap;
            align-items: center;

            :hover,
            :active,
            :focus {
                > div:last-child {
                    border: 1px solid #b5b5b5;
                }
            }`;
        }
    }}
`;

export const InputButton = styled(Button)`
    height: 50% !important;
    width: 30px !important;
    box-sizing: border-box;
    padding: 0 5px !important;
    border-left: 1px solid gray !important;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-right: 5px;
    margin-left: 5px;
    border: 0;
    border-radius: 0;
    border-color: #ccc;
    border-top-right-radius: 8px;
    border-bottom-right-radius: 8px;
    background: white;

    &:first-child {
        position: relative;
        z-index: 0;
        margin-top: 5px !important;
    }

    &:last-child {
        position: relative;
        z-index: 0;
        margin-bottom: 5px !important;
    }

    &:hover,
    &:active,
    &:focus {
        background: white !important;
    }

    ${({$inline}) => {
        if ($inline) {
            return `
            padding: 0 !important;
            margin: 0 !important;
            border: 0 !important;
            
            &:first-child {
                margin-top: 0 !important;
            }

            &:last-child {
                margin-bottom: 0 !important;
            }

            img {
                width: 25px;
            }
            `;
        }
    }}
`;

export const InputGroupAppend = styled.div<{$inline: boolean}>`
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    height: inherit !important;
    border: 2px solid ${({theme}) => theme.colors.primary.main};
    border-top-right-radius: 8px;
    border-bottom-right-radius: 8px;
    border-left: 0;

    ${({$inline}) => {
        if ($inline) {
            return `
            height: 25px !important;
            align-items: center;
            border: 1px solid transparent;
            transition: border-color 0.15s ease-in-out;
            `;
        }
    }}
`;
